import { useLazyQuery } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { RuleChange, RuleCondition } from '../../../cashflow';
import { CustomError } from '../../../errors';
import {
    TransactionEntriesForRuleConditionsDocument,
    TransactionEntryFieldsFragment,
} from '../../../graphql/generated';
import { sortBy } from '../../../util/arrays';
import { debounce } from '../../../util/debounce';
import { isNull } from '../../../util/types';
import { useFinancialEntityMap } from '../useFinancialEntityMap';
import { useTransactionEntries } from '../useTransactionEntries';
import { useTransactionEntryCategories } from '../useTransactionEntryCategories';

const useMatchingTransactions = ({
    conditions,
    changes,
    isFormValid,
    onError,
}: {
    conditions: RuleCondition[];
    changes: RuleChange[];
    isFormValid: boolean;
    onError: VoidFunction;
}) => {
    const [loading, setLoading] = useState(false);
    const [findTransactionEntriesForConditions] = useLazyQuery(
        TransactionEntriesForRuleConditionsDocument
    );
    const {
        transactionEntryCategoriesMapById,
        transactionEntryCategoriesLoading,
    } = useTransactionEntryCategories();

    const { financialEntityMap, financialEntityMapLoading } =
        useFinancialEntityMap();

    const [matchingTransactions, setMatchingTransactions] = useState<
        TransactionEntryFieldsFragment[] | null
    >(null);

    const findMatchingTransactionEntries = useMemo(
        () =>
            debounce(async (conditions: RuleCondition[]) => {
                try {
                    const { data } = await findTransactionEntriesForConditions({
                        variables: { conditions },
                    });
                    setMatchingTransactions(
                        data?.transactionEntriesForRuleConditions
                            ? sortBy(
                                  filterMatchingTransactions({
                                      transactionEntries:
                                          data.transactionEntriesForRuleConditions,
                                      changes,
                                  }),
                                  'date',
                                  'DESC'
                              )
                            : []
                    );
                } catch (e) {
                    console.error(e);
                    onError();
                    setMatchingTransactions(null);
                } finally {
                    setLoading(false);
                }
            }, 1000),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [changes]
    );

    useEffect(() => {
        if (isFormValid) {
            setLoading(true);
            findMatchingTransactionEntries(conditions);
        } else {
            setMatchingTransactions(null);
        }
        // Including findMatchingTransactionEntries here causes an infinite useEffect loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conditions, changes, isFormValid]);

    const transactionEntries = useTransactionEntries({
        transactionEntries: matchingTransactions,
        financialEntityMap,
        transactionEntryCategoriesMapById,
    });

    return {
        matchingTransactions: isNull(matchingTransactions)
            ? null
            : transactionEntries,
        loading:
            loading ||
            financialEntityMapLoading ||
            transactionEntryCategoriesLoading,
    };
};

// Avoid showing transactions that would not change because they already exactly match the changeset
const filterMatchingTransactions = ({
    transactionEntries,
    changes,
}: {
    transactionEntries: TransactionEntryFieldsFragment[];
    changes: RuleChange[];
}) =>
    transactionEntries.filter((transactionEntry) =>
        changes.some((change) => {
            const field = change.field;
            switch (field) {
                case 'merchantId':
                    return transactionEntry.merchant?.id !== change.value;
                case 'categoryId':
                    return transactionEntry.categoryId !== change.value;
                case 'tagNames': {
                    const currentTags = transactionEntry.tags.map(
                        (tag) => tag.name
                    );
                    return !change.value.every((tag) =>
                        currentTags.includes(tag)
                    );
                }
                case 'isHidden':
                    return transactionEntry.isHidden !== change.value;
                default: {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const exhaustiveCheck: never = field;
                    throw new CustomError('Unknown rule change field', {
                        field,
                    });
                }
            }
        })
    );

export default useMatchingTransactions;
